import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface TableState {
  page: number;
  limit: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: any;
}

export interface UseUrlTableReturn<T extends TableState> {
  state: T;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSearch: (search: string) => void;
  setSort: (sortBy: string, sortOrder?: 'asc' | 'desc') => void;
  setFilter: (key: string, value: string | undefined) => void;
  resetFilters: () => void;
}

/**
 * Hook to sync table state (pagination, search, filters) with URL query parameters
 * @param defaults - Default values for the table state
 * @returns Object containing current state and setter functions
 */
export function useUrlTable<T extends TableState>(defaults: T): UseUrlTableReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current state from URL params
  const state = useMemo(() => {
    const currentState: any = { ...defaults };

    // Parse page
    const pageParam = searchParams.get('page');
    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (!isNaN(page) && page > 0) {
        currentState.page = page;
      }
    }

    // Parse limit
    const limitParam = searchParams.get('limit');
    if (limitParam) {
      const limit = parseInt(limitParam, 10);
      if (!isNaN(limit) && limit > 0) {
        currentState.limit = limit;
      }
    }

    // Parse search
    const searchParam = searchParams.get('search');
    if (searchParam) {
      currentState.search = searchParam;
    }

    // Parse sortBy
    const sortByParam = searchParams.get('sortBy');
    if (sortByParam) {
      currentState.sortBy = sortByParam;
    }

    // Parse sortOrder
    const sortOrderParam = searchParams.get('sortOrder');
    if (sortOrderParam && (sortOrderParam === 'asc' || sortOrderParam === 'desc')) {
      currentState.sortOrder = sortOrderParam;
    }

    // Parse other filter keys from defaults
    Object.keys(defaults).forEach((key) => {
      if (['page', 'limit', 'search', 'sortBy', 'sortOrder'].includes(key)) {
        return;
      }
      const param = searchParams.get(key);
      if (param !== null) {
        currentState[key] = param;
      }
    });

    return currentState as T;
  }, [searchParams, defaults]);

  // Update URL params
  const updateParams = useCallback(
    (updates: Partial<T>) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Setter functions
  const setPage = useCallback(
    (page: number) => {
      updateParams({ page } as Partial<T>);
    },
    [updateParams]
  );

  const setLimit = useCallback(
    (limit: number) => {
      updateParams({ page: 1, limit } as Partial<T>); // Reset to page 1 when changing limit
    },
    [updateParams]
  );

  const setSearch = useCallback(
    (search: string) => {
      updateParams({ page: 1, search: search || undefined } as Partial<T>); // Reset to page 1 when searching
    },
    [updateParams]
  );

  const setSort = useCallback(
    (sortBy: string, sortOrder: 'asc' | 'desc' = 'asc') => {
      updateParams({ page: 1, sortBy, sortOrder } as Partial<T>);
    },
    [updateParams]
  );

  const setFilter = useCallback(
    (key: string, value: string | undefined) => {
      updateParams({ page: 1, [key]: value } as Partial<T>); // Reset to page 1 when filtering
    },
    [updateParams]
  );

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return {
    state,
    setPage,
    setLimit,
    setSearch,
    setSort,
    setFilter,
    resetFilters,
  };
}
